import { useState, useMemo, useRef, useEffect } from 'react';
import { useInventory } from '@/contexts/InventoryContext';
import { useApp } from '@/contexts/AppContext';
import { generatePOPDF, generateGRNBillPDF } from '@/utils/pdfUtils';
import { addPurchaseBill, getPurchaseBills, subscribePurchaseBills, type PurchaseBillRecord } from '@/utils/purchaseBillStore';
import type { PurchaseOrder, GoodsReceivedNote, PendingItem } from '@/contexts/InventoryContext';

type TabType = 'po-orders' | 'goods-receiving' | 'vendor-pending' | 'stock-ledger' | 'purchase-invoices';

export default function InventoryPurchases() {
  const {
    items, vendors, transactions, purchaseOrders, goodsReceivedNotes,
    createPurchaseOrder, receiveGoods, cancelPO,
    getAllPendingItems, getStockLedger, getPOById,
  } = useInventory();
  const { addToast } = useApp();

  const [activeTab, setActiveTab] = useState<TabType>('po-orders');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showPOForm, setShowPOForm] = useState(false);
  const [poForm, setPOForm] = useState({
    vendor_id: '', order_date: '', expected_delivery: '', notes: '',
    items: [{ item_id: '', qty_ordered: '', unit_price: '' }] as { item_id: string; qty_ordered: string; unit_price: string }[],
  });

  const autoPONumber = useRef('');

  const [showGRNForm, setShowGRNForm] = useState(false);
  const [grnTargetPO, setGrnTargetPO] = useState('');
  const [grnForm, setGRNForm] = useState({
    grn_number: '', received_date: '', bill_no: '', bill_amount: '', notes: '',
    items: [{ item_id: '', qty_received: '' }] as { item_id: string; qty_received: string }[],
  });

  const [showPOModal, setShowPOModal] = useState(false);
  const [viewPO, setViewPO] = useState<PurchaseOrder | null>(null);

  const [ledgerItemId, setLedgerItemId] = useState('');

  // Bill history state
  const [allBills, setAllBills] = useState<PurchaseBillRecord[]>(getPurchaseBills());
  const [billSearch, setBillSearch] = useState('');
  const [billTypeFilter, setBillTypeFilter] = useState('all');

  useEffect(() => {
    return subscribePurchaseBills(() => {
      setAllBills(getPurchaseBills());
    });
  }, []);

  // Re-download PO PDF
  const handleDownloadPO = (po: PurchaseOrder) => {
    const vendor = vendors.find((v) => v.id === po.vendor_id);
    const pdfItems = po.items.map((pi) => {
      const it = items.find((i) => i.id === pi.item_id);
      return {
        name: it?.name || 'Unknown',
        qty: pi.qty_ordered,
        unit: it?.unit || '',
        unitPrice: pi.unit_price,
        amount: pi.qty_ordered * pi.unit_price,
      };
    });
    generatePOPDF(
      po.po_number,
      po.order_date,
      po.expected_delivery || '',
      vendor?.name || 'Unknown',
      vendor?.address || '',
      vendor?.gstin || '',
      pdfItems,
      po.total_amount,
      po.notes,
    );
    addToast('success', `PO ${po.po_number} PDF downloaded`);
  };

  // Re-download GRN Invoice PDF
  const handleDownloadGRNInvoice = (grn: GoodsReceivedNote) => {
    const po = getPOById(grn.po_id);
    const vendor = vendors.find((v) => v.id === grn.vendor_id);
    const pdfItems = grn.items.map((gi) => {
      const it = items.find((i) => i.id === gi.item_id);
      const poi = po?.items.find((p) => p.item_id === gi.item_id);
      return {
        name: it?.name || 'Unknown',
        qty: gi.qty_received,
        unit: it?.unit || '',
        unitPrice: poi?.unit_price || 0,
        amount: gi.qty_received * (poi?.unit_price || 0),
      };
    });
    generateGRNBillPDF(
      grn.grn_number,
      grn.received_date,
      po?.po_number || grn.po_id,
      vendor?.name || 'Unknown',
      vendor?.address || '',
      vendor?.gstin || '',
      grn.bill_no || '',
      grn.bill_amount || 0,
      pdfItems,
      grn.notes || '',
    );
    addToast('success', `GRN Invoice ${grn.grn_number} PDF downloaded`);
  };

  const filteredBills = useMemo(() => {
    return allBills.filter((b) => {
      if (billTypeFilter !== 'all' && b.billType !== billTypeFilter) return false;
      if (billSearch) {
        const q = billSearch.toLowerCase();
        return b.number.toLowerCase().includes(q) || b.vendorName.toLowerCase().includes(q);
      }
      return true;
    });
  }, [allBills, billSearch, billTypeFilter]);

  const filteredPOs = useMemo(() => {
    return purchaseOrders.filter((po) => {
      if (statusFilter !== 'all' && po.status !== statusFilter) return false;
      if (search) {
        const vendor = vendors.find((v) => v.id === po.vendor_id);
        const q = search.toLowerCase();
        return po.po_number.toLowerCase().includes(q) || (vendor?.name.toLowerCase().includes(q));
      }
      return true;
    }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [purchaseOrders, search, statusFilter, vendors]);

  const pendingItems: PendingItem[] = useMemo(() => getAllPendingItems(), [getAllPendingItems]);

  const ledgerEntries = useMemo(() => {
    if (!ledgerItemId) return [];
    return getStockLedger(ledgerItemId);
  }, [ledgerItemId, getStockLedger]);

  const handleSavePO = () => {
    if (!poForm.vendor_id || poForm.items.length === 0) {
      addToast('error', 'Vendor and at least 1 item required');
      return;
    }
    const poItems = poForm.items
      .filter((i) => i.item_id && Number(i.qty_ordered) > 0)
      .map((i) => ({
        item_id: i.item_id,
        qty_ordered: Number(i.qty_ordered),
        qty_received: 0,
        unit_price: Number(i.unit_price) || 0,
      }));
    if (poItems.length === 0) { addToast('error', 'Add at least 1 valid item'); return; }
    const total = poItems.reduce((s, i) => s + i.qty_ordered * i.unit_price, 0);

    // Auto-generate PO number: PO-YYYYMMDD-XXX
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const todayPOs = purchaseOrders.filter((po) => po.po_number.startsWith(`PO-${today}`));
    const seq = String(todayPOs.length + 1).padStart(3, '0');
    const generatedPONumber = `PO-${today}-${seq}`;

    createPurchaseOrder({
      po_number: generatedPONumber,
      vendor_id: poForm.vendor_id,
      order_date: poForm.order_date || new Date().toISOString().slice(0, 10),
      expected_delivery: poForm.expected_delivery || '',
      status: 'ordered',
      items: poItems,
      total_amount: total,
      notes: poForm.notes,
    });

    // Generate PDF receipt
    const vendor = vendors.find((v) => v.id === poForm.vendor_id);
    const pdfItems = poItems.map((pi) => {
      const it = items.find((i) => i.id === pi.item_id);
      return {
        name: it?.name || 'Unknown',
        qty: pi.qty_ordered,
        unit: it?.unit || '',
        unitPrice: pi.unit_price,
        amount: pi.qty_ordered * pi.unit_price,
      };
    });
    generatePOPDF(
      generatedPONumber,
      poForm.order_date || new Date().toISOString().slice(0, 10),
      poForm.expected_delivery || '',
      vendor?.name || 'Unknown',
      vendor?.address || '',
      vendor?.gstin || '',
      pdfItems,
      total,
      poForm.notes,
    );

    // Store bill record for history
    addPurchaseBill({
      id: `bill-po-${Date.now()}`,
      billType: 'PO',
      number: generatedPONumber,
      date: poForm.order_date || new Date().toISOString().slice(0, 10),
      vendorName: vendor?.name || 'Unknown',
      vendorId: poForm.vendor_id,
      amount: total,
      itemCount: poItems.length,
      referenceId: '',
      createdAt: new Date().toISOString(),
    });

    addToast('success', `PO ${generatedPONumber} created! PDF downloaded.`);
    setShowPOForm(false);
    setPOForm({ vendor_id: '', order_date: '', expected_delivery: '', notes: '', items: [{ item_id: '', qty_ordered: '', unit_price: '' }] });
  };

  const handleSaveGRN = () => {
    if (!grnTargetPO || !grnForm.grn_number) { addToast('error', 'Select PO and enter GRN number'); return; }
    const po = getPOById(grnTargetPO);
    if (!po) { addToast('error', 'PO not found'); return; }
    const grnItems = grnForm.items
      .filter((i) => i.item_id && Number(i.qty_received) > 0)
      .map((i) => ({ item_id: i.item_id, qty_received: Number(i.qty_received) }));
    if (grnItems.length === 0) { addToast('error', 'Add at least 1 received item'); return; }

    for (const gi of grnItems) {
      const poi = po.items.find((p) => p.item_id === gi.item_id);
      if (poi && gi.qty_received > (poi.qty_ordered - poi.qty_received)) {
        addToast('error', `Cannot receive more than pending qty for ${items.find((it) => it.id === gi.item_id)?.name || gi.item_id}`);
        return;
      }
    }

    receiveGoods({
      grn_number: grnForm.grn_number,
      po_id: grnTargetPO,
      vendor_id: po.vendor_id,
      received_date: grnForm.received_date || new Date().toISOString().slice(0, 10),
      items: grnItems,
      bill_no: grnForm.bill_no,
      bill_amount: Number(grnForm.bill_amount) || 0,
      notes: grnForm.notes,
    });

    // Generate GRN Purchase Invoice PDF
    const grnVendor = vendors.find((v) => v.id === po.vendor_id);
    const grnPdfItems = grnItems.map((gi) => {
      const it = items.find((i) => i.id === gi.item_id);
      const poi = po.items.find((p) => p.item_id === gi.item_id);
      return {
        name: it?.name || 'Unknown',
        qty: gi.qty_received,
        unit: it?.unit || '',
        unitPrice: poi?.unit_price || 0,
        amount: gi.qty_received * (poi?.unit_price || 0),
      };
    });
    generateGRNBillPDF(
      grnForm.grn_number,
      grnForm.received_date || new Date().toISOString().slice(0, 10),
      po.po_number,
      grnVendor?.name || 'Unknown',
      grnVendor?.address || '',
      grnVendor?.gstin || '',
      grnForm.bill_no,
      Number(grnForm.bill_amount) || 0,
      grnPdfItems,
      grnForm.notes,
    );

    // Store bill record for history
    addPurchaseBill({
      id: `bill-grn-${Date.now()}`,
      billType: 'GRN',
      number: grnForm.grn_number,
      date: grnForm.received_date || new Date().toISOString().slice(0, 10),
      vendorName: grnVendor?.name || 'Unknown',
      vendorId: po.vendor_id,
      amount: Number(grnForm.bill_amount) || 0,
      itemCount: grnItems.length,
      referenceId: po.po_number,
      createdAt: new Date().toISOString(),
    });

    addToast('success', `GRN ${grnForm.grn_number} recorded — stock updated! PDF downloaded.`);
    setShowGRNForm(false);
    setGrnTargetPO('');
    setGRNForm({ grn_number: '', received_date: '', bill_no: '', bill_amount: '', notes: '', items: [{ item_id: '', qty_received: '' }] });
  };

  const getVendorName = (vendorId: string) => vendors.find((v) => v.id === vendorId)?.name || 'Unknown';
  const getItemName = (itemId: string) => items.find((i) => i.id === itemId)?.name || 'Unknown';
  const getItemUnit = (itemId: string) => items.find((i) => i.id === itemId)?.unit || '';

  const poStats = useMemo(() => ({
    total: purchaseOrders.length,
    ordered: purchaseOrders.filter((p) => p.status === 'ordered').length,
    partial: purchaseOrders.filter((p) => p.status === 'partial').length,
    received: purchaseOrders.filter((p) => p.status === 'received').length,
    totalValue: purchaseOrders.reduce((s, p) => s + p.total_amount, 0),
    pendingValue: purchaseOrders.filter((p) => p.status !== 'received' && p.status !== 'cancelled').reduce((s, po) => {
      return s + po.items.reduce((si, poi) => si + (poi.qty_ordered - poi.qty_received) * poi.unit_price, 0);
    }, 0),
  }), [purchaseOrders]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Purchases & Procurement</h1>
          <p className="text-navy-500 mt-1">Purchase Orders, Goods Receiving (GRN), Vendor Pending, and Stock Ledger.</p>
        </div>
        {activeTab === 'po-orders' && (
          <button onClick={() => {
            const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const todayPOs = purchaseOrders.filter((po) => po.po_number.startsWith(`PO-${today}`));
            const seq = String(todayPOs.length + 1).padStart(3, '0');
            autoPONumber.current = `PO-${today}-${seq}`;
            setShowPOForm(true);
          }} className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" /> Create PO
          </button>
        )}
        {activeTab === 'goods-receiving' && (
          <button onClick={() => setShowGRNForm(true)} className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-check-double-line" /> Receive Goods (GRN)
          </button>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Total POs</div>
          <div className="text-2xl font-bold text-navy-800">{poStats.total}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Ordered</div>
          <div className="text-2xl font-bold text-blue-600">{poStats.ordered}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Partial</div>
          <div className="text-2xl font-bold text-amber-600">{poStats.partial}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Received</div>
          <div className="text-2xl font-bold text-green-600">{poStats.received}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Total PO Value</div>
          <div className="text-2xl font-bold text-navy-800">Rs. {poStats.totalValue.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Pending with Vendors</div>
          <div className="text-2xl font-bold text-red-600">Rs. {poStats.pendingValue.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-navy-50 rounded-xl p-1 w-fit flex-wrap">
        {[
          { key: 'po-orders', label: '📋 Purchase Orders', count: purchaseOrders.length },
          { key: 'goods-receiving', label: '📦 Goods Receiving', count: goodsReceivedNotes.length },
          { key: 'vendor-pending', label: '⏳ Vendor Pending', count: pendingItems.length },
          { key: 'stock-ledger', label: '📒 Stock Ledger', count: null },
          { key: 'purchase-invoices', label: '🧾 Purchase Invoices', count: allBills.length },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as TabType)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.key ? 'bg-white text-navy-800 shadow-sm' : 'text-navy-500 hover:text-navy-700'
            }`}
          >
            {tab.label}{tab.count !== null && ` (${tab.count})`}
          </button>
        ))}
      </div>

      {/* ========== TAB 1: Purchase Orders ========== */}
      {activeTab === 'po-orders' && (
        <>
          <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-navy-500 mb-1.5">Search</label>
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                  <input type="text" placeholder="PO number or vendor name..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-navy-500 mb-1.5">Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  <option value="all">All</option>
                  <option value="ordered">Ordered</option>
                  <option value="partial">Partially Received</option>
                  <option value="received">Received</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {filteredPOs.map((po) => {
              const vendorName = getVendorName(po.vendor_id);
              const pendingCount = po.items.filter((i) => i.qty_received < i.qty_ordered).length;
              return (
                <div key={po.id} className="bg-white rounded-xl border border-navy-100 overflow-hidden hover:border-navy-200 transition-colors">
                  <div className="p-5">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                          po.status === 'received' ? 'bg-green-100 text-green-700' :
                          po.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                          po.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {po.status === 'received' ? '✅ RECEIVED' : po.status === 'partial' ? '⚠️ PARTIAL' : po.status === 'cancelled' ? '❌ CANCELLED' : '📝 ORDERED'}
                        </div>
                        <div>
                          <div className="font-semibold text-navy-800">{po.po_number}</div>
                          <div className="text-xs text-navy-500">{new Date(po.order_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="text-right">
                          <div className="text-xs text-navy-500">Vendor</div>
                          <div className="text-sm font-medium text-navy-800">{vendorName}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-navy-500">Expected</div>
                          <div className="text-sm font-medium text-navy-800">{po.expected_delivery ? new Date(po.expected_delivery).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-navy-500">Total</div>
                          <div className="text-sm font-bold text-navy-800">Rs. {po.total_amount.toLocaleString('en-IN')}</div>
                        </div>
                        <div className="flex gap-1.5">
                          <button onClick={() => { setViewPO(po); setShowPOModal(true); }} className="px-3 py-1.5 text-xs font-medium text-navy-600 border border-navy-200 rounded-lg hover:bg-navy-50 transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-eye-line mr-1" />View
                          </button>
                          <button onClick={() => handleDownloadPO(po)} className="px-3 py-1.5 text-xs font-medium text-green-600 border border-green-200 rounded-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-download-line mr-1" />PO PDF
                          </button>
                          {(po.status === 'ordered' || po.status === 'partial') && (
                            <button onClick={() => {
                              if (confirm(`Cancel PO ${po.po_number}?`)) cancelPO(po.id);
                            }} className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
                              <i className="ri-close-circle-line mr-1" />Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-navy-50">
                            <th className="text-left px-4 py-2 text-xs font-semibold text-navy-600">Item</th>
                            <th className="text-center px-4 py-2 text-xs font-semibold text-navy-600">Ordered</th>
                            <th className="text-center px-4 py-2 text-xs font-semibold text-navy-600">Received</th>
                            <th className="text-center px-4 py-2 text-xs font-semibold text-navy-600">Pending</th>
                            <th className="text-right px-4 py-2 text-xs font-semibold text-navy-600">Unit Price</th>
                            <th className="text-right px-4 py-2 text-xs font-semibold text-navy-600">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {po.items.map((poi, idx) => {
                            const pending = poi.qty_ordered - poi.qty_received;
                            return (
                              <tr key={idx} className="border-t border-navy-50">
                                <td className="px-4 py-2.5 text-navy-800 font-medium text-xs">{getItemName(poi.item_id)}</td>
                                <td className="px-4 py-2.5 text-center text-navy-600 text-xs">{poi.qty_ordered} {getItemUnit(poi.item_id)}</td>
                                <td className="px-4 py-2.5 text-center">
                                  <span className={`text-xs font-medium ${poi.qty_received >= poi.qty_ordered ? 'text-green-600' : 'text-amber-600'}`}>
                                    {poi.qty_received} {getItemUnit(poi.item_id)}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  {pending > 0 ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                      {pending} {getItemUnit(poi.item_id)}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-green-600">✓ Complete</span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-right text-navy-600 text-xs">Rs. {poi.unit_price}</td>
                                <td className="px-4 py-2.5 text-right text-navy-800 font-medium text-xs">Rs. {(poi.qty_ordered * poi.unit_price).toLocaleString('en-IN')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {pendingCount > 0 && (
                      <div className="mt-4 pt-4 border-t border-navy-100 flex items-center gap-3">
                        <div className="flex items-center gap-2 text-xs text-red-600">
                          <i className="ri-error-warning-line" />
                          <span>{pendingCount} item{pendingCount > 1 ? 's' : ''} still pending with vendor</span>
                        </div>
                        <button onClick={() => {
                          setGrnTargetPO(po.id);
                          const pendingOnly = po.items.filter((pi) => pi.qty_received < pi.qty_ordered);
                          setGRNForm({
                            grn_number: `GRN-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(Math.floor(Math.random() * 100)).padStart(3, '0')}`,
                            received_date: new Date().toISOString().slice(0, 10),
                            bill_no: '', bill_amount: '', notes: '',
                            items: pendingOnly.map((pi) => ({ item_id: pi.item_id, qty_received: '' })),
                          });
                          setActiveTab('goods-receiving');
                          setShowGRNForm(true);
                        }} className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                          <i className="ri-check-double-line mr-1" />Receive Now
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredPOs.length === 0 && (
              <div className="bg-white rounded-xl border border-navy-100 p-10 text-center text-navy-400">
                <i className="ri-file-list-3-line text-3xl block mb-3" />
                <p className="text-sm">No purchase orders found.</p>
                <button onClick={() => {
                  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                  const todayPOs = purchaseOrders.filter((po) => po.po_number.startsWith(`PO-${today}`));
                  const seq = String(todayPOs.length + 1).padStart(3, '0');
                  autoPONumber.current = `PO-${today}-${seq}`;
                  setShowPOForm(true);
                }} className="mt-3 text-sm text-navy-600 underline cursor-pointer">Create your first PO</button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ========== TAB 2: Goods Receiving ========== */}
      {activeTab === 'goods-receiving' && (
        <div className="space-y-4">
          {goodsReceivedNotes.slice().reverse().map((grn) => {
            const po = getPOById(grn.po_id);
            const vendorName = getVendorName(grn.vendor_id);
            return (
              <div key={grn.id} className="bg-white rounded-xl border border-navy-100 overflow-hidden">
                <div className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 whitespace-nowrap">📦 GRN</span>
                      <div>
                        <div className="font-semibold text-navy-800">{grn.grn_number}</div>
                        <div className="text-xs text-navy-500">
                          Against PO: {po?.po_number || grn.po_id} | {new Date(grn.received_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="text-right">
                        <div className="text-xs text-navy-500">Vendor</div>
                        <div className="text-sm font-medium text-navy-800">{vendorName}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-navy-500">Bill No</div>
                        <div className="text-sm font-medium text-navy-800">{grn.bill_no || '-'}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-navy-500">Amount</div>
                        <div className="text-sm font-bold text-navy-800">Rs. {grn.bill_amount.toLocaleString('en-IN')}</div>
                      </div>
                      <button onClick={() => handleDownloadGRNInvoice(grn)} className="px-3 py-1.5 text-xs font-medium text-green-600 border border-green-200 rounded-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-download-line mr-1" />Invoice PDF
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-green-50">
                          <th className="text-left px-4 py-2 text-xs font-semibold text-green-700">Item</th>
                          <th className="text-center px-4 py-2 text-xs font-semibold text-green-700">Qty Received</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grn.items.map((gi, idx) => (
                          <tr key={idx} className="border-t border-green-50">
                            <td className="px-4 py-2 text-xs text-navy-800 font-medium">{getItemName(gi.item_id)}</td>
                            <td className="px-4 py-2 text-center text-xs font-medium text-green-600">{gi.qty_received} {getItemUnit(gi.item_id)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {grn.notes && <div className="mt-3 text-xs text-navy-500 bg-navy-50 p-2 rounded"><i className="ri-chat-3-line mr-1" />{grn.notes}</div>}
                </div>
              </div>
            );
          })}
          {goodsReceivedNotes.length === 0 && (
            <div className="bg-white rounded-xl border border-navy-100 p-10 text-center text-navy-400">
              <i className="ri-inbox-line text-3xl block mb-3" />
              <p className="text-sm">No goods received yet.</p>
              <button onClick={() => setShowGRNForm(true)} className="mt-3 text-sm text-green-600 underline cursor-pointer">Record first receiving</button>
            </div>
          )}
        </div>
      )}

      {/* ========== TAB 3: Vendor Pending ========== */}
      {activeTab === 'vendor-pending' && (
        <div className="space-y-4">
          {pendingItems.length === 0 ? (
            <div className="bg-white rounded-xl border border-navy-100 p-10 text-center text-green-600">
              <i className="ri-check-double-line text-3xl block mb-3" />
              <p className="text-sm font-medium">No pending items! All POs fully received.</p>
            </div>
          ) : (
            <>
              {(() => {
                const byVendor = new Map<string, PendingItem[]>();
                pendingItems.forEach((p) => {
                  if (!byVendor.has(p.vendorId)) byVendor.set(p.vendorId, []);
                  byVendor.get(p.vendorId)!.push(p);
                });
                return Array.from(byVendor.entries()).map(([vendorId, itemsByVendor]) => {
                  const vendorName = itemsByVendor[0].vendorName;
                  const totalPending = itemsByVendor.reduce((s, pi) => {
                    const poi = purchaseOrders.find((p) => p.id === pi.poId)?.items.find((i) => i.item_id === pi.itemId);
                    return s + pi.qtyPending * (poi?.unit_price || 0);
                  }, 0);
                  return (
                    <div key={vendorId} className="bg-white rounded-xl border border-red-100 overflow-hidden">
                      <div className="p-4 border-b border-red-100 bg-red-50">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-red-800">{vendorName}</h3>
                          <span className="text-sm font-bold text-red-600">Pending Value: Rs. {totalPending.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-navy-50">
                              <th className="text-left px-4 py-2 text-xs font-semibold text-navy-600">Item</th>
                              <th className="text-left px-4 py-2 text-xs font-semibold text-navy-600">PO</th>
                              <th className="text-center px-4 py-2 text-xs font-semibold text-navy-600">Ordered</th>
                              <th className="text-center px-4 py-2 text-xs font-semibold text-navy-600">Received</th>
                              <th className="text-center px-4 py-2 text-xs font-semibold text-navy-600">Pending</th>
                              <th className="text-center px-4 py-2 text-xs font-semibold text-navy-600">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemsByVendor.map((pi, idx) => (
                              <tr key={`${pi.poId}-${pi.itemId}-${idx}`} className="border-t border-navy-50">
                                <td className="px-4 py-2.5 text-xs font-medium text-navy-800">{pi.itemName}</td>
                                <td className="px-4 py-2.5 text-xs text-navy-500">{pi.poNumber}</td>
                                <td className="px-4 py-2.5 text-center text-xs text-navy-600">{pi.qtyOrdered} {pi.unit}</td>
                                <td className="px-4 py-2.5 text-center text-xs text-green-600">{pi.qtyReceived} {pi.unit}</td>
                                <td className="px-4 py-2.5 text-center">
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
                                    {pi.qtyPending} {pi.unit}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  <button onClick={() => {
                                    setGrnTargetPO(pi.poId);
                                    setGRNForm({
                                      grn_number: `GRN-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(Math.floor(Math.random() * 100)).padStart(3, '0')}`,
                                      received_date: new Date().toISOString().slice(0, 10),
                                      bill_no: '', bill_amount: '', notes: '',
                                      items: [{ item_id: pi.itemId, qty_received: '' }],
                                    });
                                    setActiveTab('goods-receiving');
                                    setShowGRNForm(true);
                                  }} className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                                    <i className="ri-check-double-line mr-1" />Receive
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                });
              })()}
            </>
          )}
        </div>
      )}

      {/* ========== TAB 4: Stock Ledger ========== */}
      {activeTab === 'stock-ledger' && (
        <>
          <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Select Item</label>
            <select value={ledgerItemId} onChange={(e) => setLedgerItemId(e.target.value)} className="w-full max-w-md px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="">-- Choose an item to view ledger --</option>
              {items.map((item) => (
                <option key={item.id} value={item.id}>{item.name} ({item.current_qty} {item.unit} in stock)</option>
              ))}
            </select>
          </div>

          {ledgerItemId && ledgerEntries.length > 0 && (
            <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
              <div className="p-4 border-b border-navy-100">
                <h3 className="font-semibold text-navy-800">{getItemName(ledgerItemId)} — Stock Ledger</h3>
                <p className="text-xs text-navy-500 mt-0.5">Complete transaction history</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-navy-600">Date</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-navy-600">Reference</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-navy-600">Details</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-navy-600">Qty In/Out</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-navy-600">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerEntries.map((entry, idx) => (
                      <tr key={idx} className="border-t border-navy-50 hover:bg-navy-50/50">
                        <td className="px-4 py-2.5 text-xs text-navy-500 whitespace-nowrap">{entry.date}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            entry.type.includes('GRN') || entry.type.includes('Stock In') ? 'bg-green-100 text-green-700' :
                            entry.type.includes('Out') ? 'bg-amber-100 text-amber-700' :
                            'bg-navy-100 text-navy-600'
                          }`}>{entry.type}</span>
                          {entry.ref && <div className="text-xs text-navy-400 mt-0.5">{entry.ref}</div>}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-navy-600 max-w-[200px]">{entry.details}</td>
                        <td className={`px-4 py-2.5 text-center text-xs font-bold ${entry.qty >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {entry.qty >= 0 ? `+${entry.qty}` : entry.qty}
                        </td>
                        <td className={`px-4 py-2.5 text-center text-xs font-bold ${entry.balance < 0 ? 'text-red-600' : 'text-navy-800'}`}>
                          {entry.balance}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ledgerItemId && ledgerEntries.length === 0 && (
            <div className="bg-white rounded-xl border border-navy-100 p-10 text-center text-navy-400">
              <i className="ri-book-open-line text-3xl block mb-3" />
              <p className="text-sm">No transactions found for this item.</p>
            </div>
          )}

          {!ledgerItemId && (
            <div className="bg-white rounded-xl border border-navy-100 p-10 text-center text-navy-400">
              <i className="ri-book-open-line text-3xl block mb-3" />
              <p className="text-sm">Select an item above to view its complete stock ledger (all in/out movements).</p>
            </div>
          )}
        </>
      )}

      {/* ========== TAB 5: Purchase Invoices ========== */}
      {activeTab === 'purchase-invoices' && (
        <>
          <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-navy-500 mb-1.5">Search</label>
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                  <input type="text" placeholder="Invoice number or vendor name..." value={billSearch} onChange={(e) => setBillSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-navy-500 mb-1.5">Bill Type</label>
                <select value={billTypeFilter} onChange={(e) => setBillTypeFilter(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  <option value="all">All Bills</option>
                  <option value="PO">Purchase Orders</option>
                  <option value="GRN">GRN Invoices</option>
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {filteredBills.map((bill) => (
              <div key={bill.id} className="bg-white rounded-xl border border-navy-100 overflow-hidden hover:border-navy-200 transition-colors">
                <div className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                        bill.billType === 'PO' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {bill.billType === 'PO' ? '📋 PO' : '📦 GRN'}
                      </span>
                      <div>
                        <div className="font-semibold text-navy-800">{bill.number}</div>
                        <div className="text-xs text-navy-500">
                          {bill.billType === 'GRN' && bill.referenceId ? `Against PO: ${bill.referenceId} | ` : ''}
                          {new Date(bill.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="text-right">
                        <div className="text-xs text-navy-500">Vendor</div>
                        <div className="text-sm font-medium text-navy-800">{bill.vendorName}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-navy-500">Items</div>
                        <div className="text-sm font-medium text-navy-800">{bill.itemCount}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-navy-500">Amount</div>
                        <div className="text-sm font-bold text-navy-800">Rs. {bill.amount.toLocaleString('en-IN')}</div>
                      </div>
                      <button
                        onClick={() => {
                          if (bill.billType === 'PO') {
                            const po = purchaseOrders.find((p) => p.po_number === bill.number);
                            if (po) {
                              handleDownloadPO(po);
                            } else {
                              addToast('error', 'PO not found. It may have been deleted.');
                            }
                          } else {
                            const grn = goodsReceivedNotes.find((g) => g.grn_number === bill.number);
                            if (grn) {
                              handleDownloadGRNInvoice(grn);
                            } else {
                              addToast('error', 'GRN not found. It may have been deleted.');
                            }
                          }
                        }}
                        className="px-4 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-download-line mr-1" />Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredBills.length === 0 && (
              <div className="bg-white rounded-xl border border-navy-100 p-10 text-center text-navy-400">
                <i className="ri-file-text-line text-3xl block mb-3" />
                <p className="text-sm">No purchase invoices found.</p>
                <p className="text-xs mt-1 text-navy-300">Create a PO or record a GRN to generate invoices.</p>
              </div>
            )}
          </div>

          {/* Bills Summary */}
          {allBills.length > 0 && (
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
                <div className="text-2xl font-bold text-navy-800">{allBills.length}</div>
                <div className="text-xs text-navy-500 mt-1">Total Invoices</div>
              </div>
              <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{allBills.filter((b) => b.billType === 'PO').length}</div>
                <div className="text-xs text-navy-500 mt-1">PO Invoices</div>
              </div>
              <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{allBills.filter((b) => b.billType === 'GRN').length}</div>
                <div className="text-xs text-navy-500 mt-1">GRN Invoices</div>
              </div>
              <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
                <div className="text-2xl font-bold text-navy-800">Rs. {allBills.reduce((s, b) => s + b.amount, 0).toLocaleString('en-IN')}</div>
                <div className="text-xs text-navy-500 mt-1">Total Value</div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== CREATE PO MODAL ========== */}
      {showPOForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between flex-shrink-0">
              <h3 className="font-semibold text-navy-800 text-lg">Create Purchase Order</h3>
              <button onClick={() => setShowPOForm(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4 overflow-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">PO Number (Auto-generated)</label>
                  <div className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm bg-navy-50 text-navy-600 font-mono">
                    {autoPONumber.current || '(will be generated automatically)'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Vendor *</label>
                  <select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={poForm.vendor_id} onChange={(e) => setPOForm((p) => ({ ...p, vendor_id: e.target.value }))}>
                    <option value="">Select vendor</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Order Date</label>
                  <input type="date" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={poForm.order_date} onChange={(e) => setPOForm((p) => ({ ...p, order_date: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Expected Delivery</label>
                  <input type="date" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={poForm.expected_delivery} onChange={(e) => setPOForm((p) => ({ ...p, expected_delivery: e.target.value }))} />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-navy-700">Items *</label>
                  <button onClick={() => setPOForm((p) => ({ ...p, items: [...p.items, { item_id: '', qty_ordered: '', unit_price: '' }] }))} className="text-xs text-navy-600 hover:text-navy-800 cursor-pointer flex items-center gap-1">
                    <i className="ri-add-line" /> Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {poForm.items.map((pi, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <select className="flex-1 px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={pi.item_id} onChange={(e) => {
                        const updated = [...poForm.items];
                        updated[idx] = { ...updated[idx], item_id: e.target.value };
                        setPOForm((p) => ({ ...p, items: updated }));
                      }}>
                        <option value="">Select item</option>
                        {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                      </select>
                      <input type="number" placeholder="Qty" className="w-20 px-2 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={pi.qty_ordered} onChange={(e) => {
                        const updated = [...poForm.items];
                        updated[idx] = { ...updated[idx], qty_ordered: e.target.value };
                        setPOForm((p) => ({ ...p, items: updated }));
                      }} min="1" />
                      <input type="number" placeholder="Price" className="w-24 px-2 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={pi.unit_price} onChange={(e) => {
                        const updated = [...poForm.items];
                        updated[idx] = { ...updated[idx], unit_price: e.target.value };
                        setPOForm((p) => ({ ...p, items: updated }));
                      }} min="0" />
                      {poForm.items.length > 1 && (
                        <button onClick={() => {
                          setPOForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));
                        }} className="text-red-400 hover:text-red-600 cursor-pointer p-1"><i className="ri-close-line" /></button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Notes</label>
                <textarea rows={2} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" value={poForm.notes} onChange={(e) => setPOForm((p) => ({ ...p, notes: e.target.value }))} maxLength={500} />
              </div>

              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
                <i className="ri-information-line mr-1" />Purchase Order will be created with "Ordered" status. Stock will only update when goods are received via GRN.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3 flex-shrink-0">
              <button onClick={() => setShowPOForm(false)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleSavePO} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">Create PO</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== CREATE GRN MODAL ========== */}
      {showGRNForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between flex-shrink-0">
              <h3 className="font-semibold text-navy-800 text-lg">Receive Goods (GRN)</h3>
              <button onClick={() => { setShowGRNForm(false); setGrnTargetPO(''); }} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4 overflow-auto">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Select PO *</label>
                <select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={grnTargetPO} onChange={(e) => {
                  setGrnTargetPO(e.target.value);
                  if (e.target.value) {
                    const po = getPOById(e.target.value);
                    if (po) {
                      const pendingOnly = po.items.filter((pi) => pi.qty_received < pi.qty_ordered);
                      setGRNForm((p) => ({
                        ...p,
                        items: pendingOnly.map((pi) => ({ item_id: pi.item_id, qty_received: '' })),
                      }));
                    }
                  }
                }}>
                  <option value="">Select PO</option>
                  {purchaseOrders.filter((p) => p.status !== 'cancelled' && p.status !== 'received').map((po) => (
                    <option key={po.id} value={po.id}>{po.po_number} — {getVendorName(po.vendor_id)} ({po.status})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">GRN Number *</label>
                  <input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={grnForm.grn_number} onChange={(e) => setGRNForm((p) => ({ ...p, grn_number: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Received Date</label>
                  <input type="date" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={grnForm.received_date} onChange={(e) => setGRNForm((p) => ({ ...p, received_date: e.target.value }))} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Bill Number</label>
                  <input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={grnForm.bill_no} onChange={(e) => setGRNForm((p) => ({ ...p, bill_no: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Bill Amount (Rs.)</label>
                  <input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={grnForm.bill_amount} onChange={(e) => setGRNForm((p) => ({ ...p, bill_amount: e.target.value }))} min="0" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Received Items</label>
                {grnTargetPO && (
                  <div className="text-xs text-navy-400 mb-2">Only pending items from selected PO are shown below</div>
                )}
                <div className="space-y-2">
                  {grnForm.items.map((gi, idx) => {
                    const po = grnTargetPO ? getPOById(grnTargetPO) : null;
                    const poi = po?.items.find((p) => p.item_id === gi.item_id);
                    const maxPending = poi ? poi.qty_ordered - poi.qty_received : 0;
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-navy-800">{getItemName(gi.item_id)}</div>
                          {poi && <div className="text-xs text-navy-500">Ordered: {poi.qty_ordered} | Already received: {poi.qty_received} | Pending: {maxPending} {getItemUnit(gi.item_id)}</div>}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            placeholder="Qty"
                            className="w-20 px-2 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            value={gi.qty_received}
                            onChange={(e) => {
                              const updated = [...grnForm.items];
                              updated[idx] = { ...updated[idx], qty_received: e.target.value };
                              setGRNForm((p) => ({ ...p, items: updated }));
                            }}
                            min="0"
                            max={maxPending}
                          />
                          <span className="text-xs text-navy-500">{getItemUnit(gi.item_id)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Notes</label>
                <textarea rows={2} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" value={grnForm.notes} onChange={(e) => setGRNForm((p) => ({ ...p, notes: e.target.value }))} maxLength={500} />
              </div>

              <div className="p-3 bg-green-50 border border-green-100 rounded-lg text-xs text-green-700">
                <i className="ri-information-line mr-1" />Recording GRN will automatically update stock quantities and PO status.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3 flex-shrink-0">
              <button onClick={() => { setShowGRNForm(false); setGrnTargetPO(''); }} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleSaveGRN} className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer">Record GRN & Update Stock</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== VIEW PO DETAIL MODAL ========== */}
      {showPOModal && viewPO && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">{viewPO.po_number}</h3>
              <button onClick={() => { setShowPOModal(false); setViewPO(null); }} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-navy-500">Vendor</span><span className="font-medium text-navy-800">{getVendorName(viewPO.vendor_id)}</span></div>
              <div className="flex justify-between"><span className="text-navy-500">Status</span><span className="font-medium capitalize text-navy-800">{viewPO.status}</span></div>
              <div className="flex justify-between"><span className="text-navy-500">Order Date</span><span className="text-navy-700">{new Date(viewPO.order_date).toLocaleDateString('en-IN')}</span></div>
              <div className="flex justify-between"><span className="text-navy-500">Expected Delivery</span><span className="text-navy-700">{viewPO.expected_delivery ? new Date(viewPO.expected_delivery).toLocaleDateString('en-IN') : '-'}</span></div>
              <div className="flex justify-between"><span className="text-navy-500">Total Amount</span><span className="font-bold text-navy-800">Rs. {viewPO.total_amount.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between"><span className="text-navy-500">Notes</span><span className="text-navy-700 text-right max-w-[60%]">{viewPO.notes || '-'}</span></div>
              <div className="pt-3 border-t border-navy-100">
                <div className="font-medium text-navy-800 mb-2">Items</div>
                <div className="space-y-2">
                  {viewPO.items.map((poi, idx) => (
                    <div key={idx} className="flex justify-between text-xs">
                      <span className="text-navy-800">{getItemName(poi.item_id)}</span>
                      <span className="text-navy-600">{poi.qty_ordered} {getItemUnit(poi.item_id)} x Rs.{poi.unit_price}</span>
                      <span className="font-medium text-navy-800">Rs. {(poi.qty_ordered * poi.unit_price).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}